import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import { colors } from '../../theme/theme';
import { AmortizationScheduleResponse } from '../IMessageBubbleInterface';
import { formatCurrency, formatDate } from '../MessageBubbleHelpers';

export const AmortizationScheduleCard: React.FC<{
    amortizationScheduleData: AmortizationScheduleResponse;
}> = ({ amortizationScheduleData }) => (
    <Box>
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
                mb: 0.9,
                p: 0.9,
                borderRadius: '10px',
                backgroundColor: '#F4F7FF',
                border: `1px solid ${colors.bluePreset[30]}`,
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    variant="body2"
                    sx={{
                        color: colors.greyPreset[50],
                        fontWeight: 600,
                        fontSize: '0.78rem',
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                    }}
                >
                    Amortization Schedule
                </Typography>
                <Typography variant="body2" sx={{ color: colors.fullBlack, fontWeight: 700, fontSize: '0.95rem' }}>
                    {amortizationScheduleData.contractId
                        ? `Contract ID: ${amortizationScheduleData.contractId}`
                        : amortizationScheduleData.intro}
                </Typography>
                {amortizationScheduleData.contractName && (
                    <Typography variant="caption" sx={{ color: colors.greyPreset[60], fontWeight: 500 }}>
                        {amortizationScheduleData.contractName}
                    </Typography>
                )}
            </Box>
        </Stack>

        {amortizationScheduleData.summaryLine && (
            <Box
                sx={{
                    mb: 1,
                    p: 1,
                    borderRadius: '12px',
                    border: `1px solid ${colors.greyPreset[30]}`,
                    backgroundColor: colors.fullWhite,
                }}
            >
                <Typography variant="body2" sx={{ color: colors.fullBlack, lineHeight: 1.6 }}>
                    {amortizationScheduleData.summaryLine}
                </Typography>
            </Box>
        )}

        {amortizationScheduleData.stats.length > 0 && (
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                    gap: 0.8,
                    mb: 1,
                }}
            >
                {amortizationScheduleData.stats.slice(0, 3).map(({ label, value }) => (
                    <Box
                        key={label}
                        sx={{
                            p: 1,
                            borderRadius: '12px',
                            border: `1px solid ${colors.greyPreset[30]}`,
                            backgroundColor: colors.greyPreset[5],
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{ color: colors.greyPreset[60], fontWeight: 600, display: 'block', mb: 0.2 }}
                        >
                            {label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.fullBlack, fontWeight: 700 }}>
                            {/^\$?[\d,.]+$/.test((value || '').trim()) ? formatCurrency(value) : value}
                        </Typography>
                    </Box>
                ))}
            </Box>
        )}

        {amortizationScheduleData.upcomingPayments.length > 0 && (
            <Stack gap={0.8} sx={{ mb: amortizationScheduleData.followUp ? 1 : 0 }}>
                {amortizationScheduleData.upcomingPayments.map((item, index) => (
                    <Box
                        key={`amortization-${index}`}
                        sx={{
                            border: `1px solid ${colors.bluePreset[30]}`,
                            borderRadius: '12px',
                            backgroundColor: colors.fullWhite,
                            p: 1,
                            boxShadow: '0 1px 5px rgba(16, 24, 40, 0.06)',
                        }}
                    >
                        <Stack gap={0.8}>
                            <Typography variant="body2" sx={{ color: colors.fullBlack, fontWeight: 700 }}>
                                {item.paymentDate ? formatDate(item.paymentDate) : '-'}
                            </Typography>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                                    gap: 0.8,
                                }}
                            >
                                {[
                                    ['Payment', item.payment],
                                    ['Source', item.source],
                                    ['Principal', item.principal],
                                    ['Interest', item.interest],
                                    ['Balance', item.remainingBalance],
                                ].map(([label, value]) => (
                                    <Box key={`${label}-${index}`} sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: colors.greyPreset[60],
                                                fontWeight: 600,
                                                display: 'block',
                                                mb: 0.2,
                                            }}
                                        >
                                            {label}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: colors.fullBlack,
                                                fontWeight: 700,
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {label === 'Source'
                                                ? value || '-'
                                                : value && value !== '-'
                                                  ? formatCurrency(value)
                                                  : '-'}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Stack>
                    </Box>
                ))}
            </Stack>
        )}

        {amortizationScheduleData.followUp && (
            <Box
                sx={{
                    mt: 1,
                    p: 1,
                    borderRadius: '12px',
                    border: `1px solid ${colors.bluePreset[30]}`,
                    backgroundColor: '#EAF2FF',
                }}
            >
                <Typography variant="body2" sx={{ color: colors.bluePreset[10], fontWeight: 600, lineHeight: 1.6 }}>
                    {amortizationScheduleData.followUp}
                </Typography>
            </Box>
        )}
    </Box>
);
